/**
 * Lonceng pemberitahuan — MURNI, tanpa React.
 *
 * Menentukan apa yang DITAMPILKAN, bukan apa yang diizinkan. Sama seperti `navigation.ts` dan
 * `correction-decision.ts`: penegakan ada di API, dan setiap halaman tetap aman bila URL-nya
 * dipaksa dibuka.
 *
 * Loncengnya membawa DUA hal yang sifatnya berbeda, dan perbedaan itu menentukan cara
 * menghitungnya — lihat `pemberitahuan.ts`.
 */

import { hasAnyPermission } from './navigation';

/**
 * Izin yang membuat pengajuan koreksi masuk lonceng.
 *
 * Sengaja SAMA dengan izin yang memunculkan tombol Setujui/Tolak di `correction-decision.ts`.
 * Lonceng yang berbunyi untuk orang yang tidak dapat memutuskan apa pun hanyalah gangguan: kurir
 * akan melihat angka merah setiap hari, menekannya, lalu menemukan daftar yang tidak dapat ia
 * apa-apakan — dan lonceng yang selalu diabaikan tidak akan diperhatikan saat benar-benar perlu.
 */
export const IZIN_MEMUTUSKAN_KOREKSI = [
  'workforce.attendance.correction.approve',
  'workforce.attendance.correction.reject',
] as const;

/**
 * Izin yang membuat penyelesaian tugas masuk lonceng.
 *
 * Sengaja izin MENGELOLA tugas, bukan `workforce.task.read`. Kurir pun punya `task.read` — ia
 * memakainya untuk melihat tugasnya sendiri — dan memberitahunya bahwa ia baru saja menyelesaikan
 * tugas adalah memberi tahu orang tentang perbuatannya sendiri, semenit setelah ia melakukannya.
 * Yang perlu tahu adalah orang yang memberangkatkannya.
 */
export const IZIN_MENGELOLA_TUGAS = ['workforce.task.assign', 'workforce.task.create'] as const;

export function belTerlihat(permissions: readonly string[]): boolean {
  return dapatMemutuskanKoreksi(permissions) || dapatMengelolaTugas(permissions);
}

export function dapatMemutuskanKoreksi(permissions: readonly string[]): boolean {
  return hasAnyPermission(permissions, [...IZIN_MEMUTUSKAN_KOREKSI]);
}

export function dapatMengelolaTugas(permissions: readonly string[]): boolean {
  return hasAnyPermission(permissions, [...IZIN_MENGELOLA_TUGAS]);
}

/**
 * Angka pada lonceng. Di atas 9 ditulis "9+".
 *
 * Bukan kerapian: lencana ini melayang di bilah atas yang sempit, dan angka tiga digit akan
 * melebar menutupi judulnya. Yang perlu dibaca sekilas adalah "ada yang menunggu, dan banyak" —
 * jumlah persisnya ada di halamannya sendiri.
 */
export function tampilanJumlah(jumlah: number): string {
  if (!Number.isFinite(jumlah) || jumlah <= 0) return '';
  return jumlah > 9 ? '9+' : String(Math.floor(jumlah));
}

/**
 * Keterangan untuk pembaca layar DAN tooltip.
 *
 * Menyebut angka utuh meski lencananya menulis "9+" — pembatasan tadi murni soal lebar, dan
 * pembaca layar tidak punya masalah lebar. Kedua jenis disebut TERPISAH: "3 pemberitahuan" tidak
 * memberi tahu apakah yang menunggu keputusan atau sekadar kabar selesai, padahal yang pertama
 * menuntut tindakan dan yang kedua tidak.
 */
export function labelBel(koreksi: number, selesai: number): string {
  const k = bulatkan(koreksi);
  const s = bulatkan(selesai);
  if (k === 0 && s === 0) return 'Tidak ada pemberitahuan baru';

  const bagian: string[] = [];
  if (k > 0) bagian.push(`${k} pengajuan koreksi menunggu keputusan`);
  if (s > 0) bagian.push(`${s} tugas baru diselesaikan kurir`);
  return bagian.join(', ');
}

function bulatkan(n: number): number {
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}
